import { getCustomizeConfig } from '../services/pdfRenderService';
import { isIos, isSafari, isMobile, isHarmonyOS } from '@umu/umu-ua';
/**
 * debugLog：开发/测试环境日志输出
 * 设计目标：调用方式与 `console.log` 一致（支持任意参数、格式化字符串、对象引用等）。
 * 生产环境会被条件编译剔除（避免日志与性能开销）。
 */
let isDebugLog = window.location.search.includes('debug_log=1') || (window as any).debugger;
isDebugLog = true;
export const debugLog = (...args: unknown[]) => {
    // eslint-disable-next-line no-console
    isDebugLog && console.log('[pdfjs debugLog] ', ...args);
};

/**
 * iOS/Safari 环境标识（模块级缓存，避免重复 UA 解析）
 *
 * 说明：iOS Safari 使用 IOSurface（GPU 纹理）支撑 canvas，存在两个独立限制层：
 * 1. 单 canvas 超限 → WebKit 去加速，canvas 内容变白（无 JS 错误）
 * 2. 多 canvas 总 IOSurface 超出进程预算 → iOS jetsam 杀进程，标签页白屏崩溃
 * 这不是"低内存设备"专属问题，所有 iOS 设备（含最新高端机）均适用。
 */
export const isIOSSafari = isIos || isSafari || isMobile || isHarmonyOS();

/**
 * 鸿蒙 OS 环境标识（模块级缓存）。用于针对鸿蒙特有兼容性问题的专项处理。
 *
 * 检测两类情形：
 * 1. UA 含显式鸿蒙标识（harmonyos / ohos / openharmony）
 * 2. HarmonyOS 4.x 以 "Android 12" 伪装，UA 不含鸿蒙字样，
 *    但 Build 串含 HUAWEI 且运行在微信 WebView 中——正是 Worker 崩溃的场景。
 */
export const isHarmonyOSDevice = (() => {
    if (typeof navigator === 'undefined') return false;
    if (isHarmonyOS()) return true;
    const ua = navigator.userAgent;
    return /android/i.test(ua) && /huawei/i.test(ua) && /MicroMessenger/i.test(ua);
})();

/**
 * iOS/Safari：IOSurface 单 canvas 上限约 16M 物理像素，最小保持 2 规避白屏。
 * 非 iOS/Safari：最小提升至 3，改善扫描件初始渲染清晰度（≈289 DPI @ A4）；
 *   超大页面由 tile 分块渲染处理，pixelRatio 不会因超限而降级。
 */
const MIN_PIXEL_RATIO_IOS = 1;
const MIN_PIXEL_RATIO_STD = 2;

export const getPixelRatio = () => {
    const dpr = Math.floor(window.devicePixelRatio || 1);
    const minRatio = isIos || isSafari ? MIN_PIXEL_RATIO_IOS : MIN_PIXEL_RATIO_STD;
    return Math.max(minRatio, dpr);
};

export const onDocumentContextMenu = (e: React.SyntheticEvent) => {
    e.preventDefault();
    return false;
};

export const getWaterMarkText = async (): Promise<IWaterMarkProps> => {
    const { data = {} } = await getCustomizeConfig(true);
    return (
        data?.watermark || {
            text: '',
        }
    );
};

/**
 * 将数组分批处理
 *
 * @param array - 要分批的数组
 * @param size - 每批的大小
 * @returns 分批后的二维数组
 *
 * @example
 * ```ts
 * chunkArray([1, 2, 3, 4, 5], 2) // [[1, 2], [3, 4], [5]]
 * ```
 */
export const chunkArray = <T>(array: T[], size: number): T[][] => {
    if (!Array.isArray(array)) {
        return [];
    }
    if (typeof size !== 'number' || size <= 0 || !Number.isFinite(size)) {
        return [array];
    }

    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
        chunks.push(array.slice(i, i + size));
    }
    return chunks;
};
